import { Button, Typography } from '@material-tailwind/react';
import {
  COURSES,
  CurriculumGroup,
  FREE_TRIAL_GUIDE_ITEMS,
} from '../../constants/types/free-trial/type';
import { ChevronDownIcon } from '@heroicons/react/24/outline';
import { useFreeTrialConfig } from '../../hooks/useFreeTrialConfig';
import { useState } from 'react';

/* 정보 영역 */
const FreeTrialInfoSection = () => {
  const config = useFreeTrialConfig();

  const [openCourseTitle, setOpenCourseTitle] = useState<string | null>(null);

  const handleToggleCurriculum = (courseTitle: string) => {
    setOpenCourseTitle((prev) => (prev === courseTitle ? null : courseTitle));
  };

  const selectedCourse = COURSES.find(
    (course) => course.title === openCourseTitle,
  );

  const openBlogReviewLink = () => {
    window.open(config.FREE_TRIAL_BLOG_REVIEW, '_blank');
  };

  const openPrimaryBlogReviewLink = () => {
    window.open(config.FREE_TRIAL_PRIMARY_BLOG_REVIEW, '_blank');
  };

  const openYoutubeReviewLink = () => {
    window.open(config.FREE_TRIAL_YOUTUBE_REVIEW, '_blank');
  };

  const openPrimaryYoutubeReviewLink = () => {
    window.open(config.FREE_TRIAL_PRIMARY_YOUTUBE_REVIEW, '_blank');
  };

  const openSeminarVideoLink = () => {
    window.open(config.FREE_TRIAL_SEMINAR_VIDEO, '_blank');
  };

  const openKakaoLink = () => {
    window.open(config.FREE_TRIAL_KAKAO, '_blank');
  };

  return (
    <>
      {/* 과목 소개 */}
      <section className="mt-14">
        <Typography className="text-[12px] font-bold text-[#6737F4]">
          베이직 코스
        </Typography>

        <Typography className="mt-1 text-[22px] font-black text-gray-900 md:text-[24px]">
          잉스바와 함께 배우는 과목들
        </Typography>

        <div className="mt-5 grid gap-3 md:grid-cols-3">
          {COURSES.map((course) => {
            const isOpen = openCourseTitle === course.title;

            return (
              <div key={course.title} className="contents">
                <div
                  className={`rounded-2xl border p-5 transition-colors ${
                    isOpen
                      ? 'border-[#8E6DFF] bg-[#F7F4FF]'
                      : 'border-[#E8E2FA] bg-white'
                  }`}
                >
                  <div className="flex items-start justify-between gap-2">
                    <Typography className="text-[17px] font-extrabold text-gray-900 md:text-[18px]">
                      {course.title}
                    </Typography>

                    <span
                      className={`rounded-full px-2.5 py-1 text-[10px] font-bold md:text-[11px] ${
                        course.isActive
                          ? 'bg-[#FFD928] text-gray-900'
                          : 'bg-[#F4F0FF] text-[#6737F4]'
                      }`}
                    >
                      {course.badge}
                    </span>
                  </div>

                  <Typography className="mt-3 min-h-[40px] text-[12px] leading-5 text-gray-700 md:text-[13px] md:leading-6">
                    {course.description}
                  </Typography>

                  <button
                    type="button"
                    onClick={() => handleToggleCurriculum(course.title)}
                    className="mt-4 flex items-center gap-1 text-[11px] font-bold text-[#6737F4]"
                  >
                    {isOpen ? '커리큘럼 접기' : '커리큘럼 보기'}

                    <ChevronDownIcon
                      className={`h-3.5 w-3.5 transition-transform ${
                        isOpen ? 'rotate-180' : ''
                      }`}
                    />
                  </button>
                </div>

                {/* 모바일: 선택한 카드 바로 아래 */}
                {isOpen && (
                  <div className="md:hidden">
                    <CurriculumDetail
                      course={course}
                      onClose={() => setOpenCourseTitle(null)}
                    />
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {selectedCourse && (
          <div className="mt-3 hidden md:block">
            <CurriculumDetail
              course={selectedCourse}
              onClose={() => setOpenCourseTitle(null)}
            />
          </div>
        )}
      </section>

      {/* 더 알아보기 */}
      <section className="mt-12">
        <Typography className="text-[22px] font-black text-gray-900 md:text-[24px]">
          젭스, 더 알아보기
        </Typography>

        <div className="mt-5 grid items-stretch gap-3 md:grid-cols-3">
          {/* 후기 */}
          <div className="flex flex-col rounded-2xl bg-white p-5">
            <div className="flex-1">
              <Typography className="text-[11px] font-bold text-[#6737F4] md:text-[12px]">
                먼저 해 본 가정의 후기
              </Typography>

              <div className="mt-3 flex flex-col gap-1">
                {/* 블로그 후기 */}
                <button
                  type="button"
                  className="flex items-center gap-2.5 rounded-xl p-1 text-left transition-colors duration-200 hover:bg-gray-100"
                  onClick={openPrimaryBlogReviewLink}
                >
                  <div className="h-12 w-12 shrink-0 overflow-hidden rounded-lg bg-gray-100">
                    <img
                      src="/svg/icon-blog-on.svg"
                      alt="네이버 블로그"
                      className="h-full w-full object-cover"
                    />
                  </div>

                  <div className="min-w-0">
                    <Typography className="mb-0.5 text-[9px] font-bold text-green-500 md:text-[10px]">
                      NAVER BLOG
                    </Typography>

                    <Typography className="line-clamp-2 text-[12px] font-medium leading-[1.45] text-gray-700 md:text-[13px]">
                      {config.FREE_TRIAL_PRIMARY_BLOG_TITLE}
                    </Typography>
                  </div>
                </button>

                {/* 유튜브 후기 */}
                <button
                  type="button"
                  className="flex items-center gap-2.5 rounded-xl p-1 text-left transition-colors duration-200 hover:bg-gray-100"
                  onClick={openPrimaryYoutubeReviewLink}
                >
                  <div className="h-12 w-12 shrink-0 overflow-hidden rounded-lg bg-gray-100">
                    <img
                      src="/svg/icon-youtube-on.svg"
                      alt="유튜브"
                      className="h-full w-full object-cover"
                    />
                  </div>

                  <div className="min-w-0">
                    <Typography className="mb-0.5 text-[9px] font-bold text-red-500 md:text-[10px]">
                      YOUTUBE
                    </Typography>

                    <Typography className="line-clamp-2 text-[12px] font-medium leading-[1.45] text-gray-700 md:text-[13px]">
                      {config.FREE_TRIAL_PRIMARY_YOUTUBE_TITLE}
                    </Typography>
                  </div>
                </button>
              </div>
            </div>

            <div className="mt-5 grid grid-cols-2 gap-2">
              <Button
                variant="outlined"
                ripple={false}
                className="h-10 rounded-xl border-[#B9A6FF] px-2 py-0 text-[12px] font-bold normal-case text-[#6737F4] shadow-none hover:shadow-none focus:shadow-none"
                onClick={openBlogReviewLink}
              >
                블로그 후기
              </Button>

              <Button
                variant="outlined"
                ripple={false}
                className="h-10 rounded-xl border-[#B9A6FF] px-2 py-0 text-[12px] font-bold normal-case text-[#6737F4] shadow-none hover:shadow-none focus:shadow-none"
                onClick={openYoutubeReviewLink}
              >
                유튜브 후기
              </Button>
            </div>
          </div>

          {/* 운영 설명 */}
          <div className="flex flex-col rounded-2xl bg-white p-5">
            <div className="flex-1">
              <Typography className="text-[11px] font-bold text-[#6737F4] md:text-[12px]">
                운영 설명
              </Typography>

              <Typography className="mt-2 text-[17px] font-extrabold leading-6 text-gray-900 md:text-[18px]">
                젭스 커리큘럼과 운영 방법을
                <br />
                영상으로 들어 보세요
              </Typography>

              <Typography className="mt-3 text-[12px] leading-5 text-gray-700 md:text-[13px] md:leading-6">
                신청부터 실제 수업까지 운영 방법을 한 번에 확인할 수 있어요.
              </Typography>
            </div>

            <Button
              ripple={false}
              className="mt-5 h-10 w-full shrink-0 rounded-xl bg-[#6737F4] py-0 text-[13px] font-bold normal-case text-white shadow-none hover:bg-[#5D2EE7] hover:shadow-none focus:shadow-none"
              onClick={openSeminarVideoLink}
            >
              설명회 영상보기
            </Button>
          </div>

          {/* 1:1 상담 */}
          <div className="flex flex-col rounded-2xl bg-white p-5">
            <div className="flex-1">
              <Typography className="text-[11px] font-bold text-[#6737F4] md:text-[12px]">
                1:1 상담
              </Typography>

              <Typography className="mt-2 text-[17px] font-extrabold leading-6 text-gray-900 md:text-[18px]">
                우리 아이에게 맞는 젭스 레벨을
                <br />
                찾아 드려요
              </Typography>

              <Typography className="mt-3 text-[12px] leading-5 text-gray-700 md:text-[13px] md:leading-6">
                체험 관련 궁금한 점을 카카오톡으로 편하게 물어보세요.
              </Typography>
            </div>

            <Button
              ripple={false}
              className="mt-5 h-10 w-full shrink-0 rounded-xl bg-[#FFD928] py-0 text-[13px] font-bold normal-case text-[#493600] shadow-none hover:shadow-none focus:shadow-none"
              onClick={openKakaoLink}
            >
              카카오톡 상담하기
            </Button>
          </div>
        </div>
      </section>

      {/* 안내 */}
      <section className="mt-10 rounded-2xl bg-white px-5 py-6 md:px-7">
        <Typography className="mb-5 text-[16px] font-extrabold text-gray-900">
          무료체험 안내
        </Typography>

        <div className="space-y-4">
          {FREE_TRIAL_GUIDE_ITEMS.map((item) => (
            <div
              key={item.label}
              className="flex flex-col gap-2 md:flex-row md:gap-6"
            >
              <span className="w-fit rounded-full bg-[#F4F0FF] px-3 py-1 text-[11px] font-bold text-[#6737F4] md:w-[82px] md:text-center md:text-[12px]">
                {item.label}
              </span>

              <Typography className="flex-1 text-[12px] leading-5 text-gray-700 md:text-[13px] md:leading-6">
                {item.description}
              </Typography>
            </div>
          ))}
        </div>
      </section>
    </>
  );
};

export default FreeTrialInfoSection;

const CurriculumDetail = ({
  course,
  onClose,
}: {
  course: (typeof COURSES)[number];
  onClose: () => void;
}) => {
  return (
    <div className="rounded-2xl border border-[#9B7DFF] bg-white p-4 md:p-5">
      <div className="flex items-center justify-between gap-4">
        <Typography className="text-[16px] font-extrabold text-gray-900 md:text-[18px]">
          {course.title} 커리큘럼
        </Typography>

        <button
          type="button"
          onClick={onClose}
          className="flex shrink-0 items-center gap-1 text-[11px] font-bold text-[#6737F4]"
        >
          접기
          <ChevronDownIcon className="h-3.5 w-3.5 rotate-180" />
        </button>
      </div>

      <Typography className="mt-4 text-[12px] leading-6 text-gray-700 md:text-[13px]">
        {course.curriculumDescription}
      </Typography>

      <div className="mt-4 grid gap-3 md:grid-cols-2">
        {course.curriculums.map((curriculum) => (
          <CurriculumCard key={curriculum.id} curriculum={curriculum} />
        ))}
      </div>
    </div>
  );
};

interface CurriculumCardProps {
  curriculum: CurriculumGroup;
}

const CurriculumCard = ({ curriculum }: CurriculumCardProps) => {
  const isPurple = curriculum.variant === 'PURPLE';

  return (
    <div className="flex h-[420px] min-w-0 flex-col overflow-hidden rounded-xl border border-[#DED8F2] bg-white">
      {/* 헤더 */}
      <div
        className={`shrink-0 px-4 py-3 ${
          isPurple ? 'bg-[#4322B8] text-white' : 'bg-[#FFF3D5] text-[#3A2A18]'
        }`}
      >
        <Typography className="text-[14px] font-extrabold md:text-[15px]">
          {curriculum.title}
        </Typography>

        <Typography
          className={`mt-0.5 text-[9px] font-bold ${
            isPurple ? 'text-white/80' : 'text-[#6B5537]'
          }`}
        >
          {curriculum.range}
        </Typography>
      </div>

      {/* 커리큘럼 목록 */}
      <div className="min-h-0 flex-1 overflow-y-auto">
        {curriculum.levels.map((level) => (
          <div key={level.level}>
            {/* Level */}
            <div className="sticky top-0 z-10 border-b border-[#E6E0F7] bg-[#F4F0FF] px-3 py-1.5">
              <Typography className="text-[9px] font-extrabold text-[#6737F4] md:text-[10px]">
                {level.level}
              </Typography>
            </div>

            {/* Book 목록 */}
            {level.books.map((book) => (
              <div
                key={`${level.level}-${book.book}`}
                className="flex min-h-[42px] items-start gap-3 border-t border-[#EEEAF8] px-3 py-2.5"
              >
                <span className="mt-0.5 shrink-0 rounded-md bg-[#F1EDFF] px-2 py-1 text-[9px] font-extrabold text-[#6737F4]">
                  {book.book}
                </span>

                <div className="min-w-0 flex-1">
                  <Typography className="text-[10px] font-bold leading-4 text-gray-800 md:text-[11px]">
                    {book.title}
                  </Typography>

                  {book.description && (
                    <Typography className="mt-0.5 text-[9px] leading-4 text-gray-700 md:text-[10px]">
                      {book.description}
                    </Typography>
                  )}

                  {book.tags && (
                    <div className="mt-1.5 flex flex-wrap gap-1">
                      {book.tags.map((tag) => (
                        <span
                          key={tag}
                          className="rounded-full bg-[#F5F2FF] px-2 py-0.5 text-[8px] font-semibold leading-4 text-[#675A87] md:text-[9px]"
                        >
                          {tag}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
};
